export const QR_TYPES = ['url', 'text', 'wifi', 'vcard', 'email', 'sms', 'phone'] as const

export type QrType = (typeof QR_TYPES)[number]
export type QrLandingSource = 'direct' | 'legacy_redirect'
export type ErrorCorrection = 'L' | 'M' | 'Q' | 'H'
export type WifiEncryption = 'WPA' | 'WEP' | 'nopass'
export type QrAnalyticsKey =
  | 'qr-generator'
  | 'url-qr'
  | 'text-qr'
  | 'wifi-qr'
  | 'vcard-qr'
  | 'email-qr'
  | 'sms-qr'
  | 'phone-qr'

const LEGACY_ANALYTICS_KEY_BY_TYPE = {
  url: 'url-qr',
  text: 'text-qr',
  wifi: 'wifi-qr',
  vcard: 'vcard-qr',
  email: 'email-qr',
  sms: 'sms-qr',
  phone: 'phone-qr',
} as const satisfies Record<QrType, QrAnalyticsKey>

export const LEGACY_QR_ROUTE_SOURCE = 'legacy-payload-route'

export interface QrEntry {
  type: QrType
  landingSource: QrLandingSource
}

export interface FormState {
  type: QrType
  url: string
  text: string
  wifiSsid: string
  wifiPassword: string
  wifiEncryption: WifiEncryption
  wifiHidden: boolean
  vcardName: string
  vcardOrg: string
  vcardPhone: string
  vcardEmail: string
  vcardUrl: string
  emailAddress: string
  emailSubject: string
  emailBody: string
  smsPhone: string
  smsBody: string
  phone: string
  foreground: string
  background: string
  size: number
  correction: ErrorCorrection
}

export interface QrErrorMessages {
  urlRequired: string
  invalidUrl: string
  textRequired: string
  wifiSsidRequired: string
  emptyVcard: string
  emailRequired: string
  smsPhoneRequired: string
  phoneRequired: string
}

export class QrInputError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message)
  }
}

export function createInitialForm(type: QrType = 'url'): FormState {
  return {
    type,
    url: 'https://qrka.ru',
    text: 'Привет! Это статический QR-код.',
    wifiSsid: '',
    wifiPassword: '',
    wifiEncryption: 'WPA',
    wifiHidden: false,
    vcardName: '',
    vcardOrg: '',
    vcardPhone: '',
    vcardEmail: '',
    vcardUrl: '',
    emailAddress: '',
    emailSubject: '',
    emailBody: '',
    smsPhone: '',
    smsBody: '',
    phone: '',
    foreground: '#111827',
    background: '#ffffff',
    size: 512,
    correction: 'Q',
  }
}

export function qrEntryFromSearch(search: string): QrEntry {
  const params = new URLSearchParams(search)
  const candidate = params.get('type')
  const type = QR_TYPES.find((value) => value === candidate) ?? 'url'
  const landingSource =
    candidate === type && params.get('source') === LEGACY_QR_ROUTE_SOURCE
      ? 'legacy_redirect'
      : 'direct'
  return { type, landingSource }
}

export function qrTypeFromSearch(search: string): QrType {
  return qrEntryFromSearch(search).type
}

export function qrTypeSearch(type: QrType): string {
  return `?type=${type}`
}

export function qrAnalytics(
  form: Pick<FormState, 'type'>,
  landingSource: QrLandingSource,
): { qr_type: QrType; landing_source: QrLandingSource } {
  return {
    qr_type: form.type,
    landing_source: landingSource,
  }
}

export function qrAnalyticsKey(
  type: QrType,
  landingSource: QrLandingSource,
): QrAnalyticsKey {
  return landingSource === 'legacy_redirect'
    ? LEGACY_ANALYTICS_KEY_BY_TYPE[type]
    : 'qr-generator'
}

export function buildPayload(form: FormState, errors: QrErrorMessages): string {
  switch (form.type) {
    case 'url':
      return normalizeUrl(form.url, errors)
    case 'text':
      return required(form.text, errors.textRequired)
    case 'wifi':
      return buildWifiPayload(form, errors)
    case 'vcard':
      return buildVcardPayload(form, errors)
    case 'email':
      return buildEmailPayload(form, errors)
    case 'sms':
      return buildSmsPayload(form, errors)
    case 'phone':
      return `tel:${required(form.phone, errors.phoneRequired)}`
  }
}

function normalizeUrl(value: string, errors: QrErrorMessages): string {
  const trimmed = required(value, errors.urlRequired)
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`

  try {
    const url = new URL(withScheme)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      throw new Error('Unsupported protocol')
    }
    return url.toString()
  } catch {
    throw new QrInputError('invalid_url', errors.invalidUrl)
  }
}

function buildWifiPayload(form: FormState, errors: QrErrorMessages): string {
  const ssid = required(form.wifiSsid, errors.wifiSsidRequired)
  const type = form.wifiEncryption
  const password = type === 'nopass' ? '' : `P:${escapeWifi(form.wifiPassword)};`
  return `WIFI:T:${type};S:${escapeWifi(ssid)};${password}H:${form.wifiHidden ? 'true' : 'false'};;`
}

function buildVcardPayload(form: FormState, errors: QrErrorMessages): string {
  if (!form.vcardName && !form.vcardOrg && !form.vcardPhone && !form.vcardEmail && !form.vcardUrl) {
    throw new QrInputError('empty_vcard', errors.emptyVcard)
  }

  const url = form.vcardUrl ? normalizeUrl(form.vcardUrl, errors) : null

  return [
    'BEGIN:VCARD',
    'VERSION:3.0',
    form.vcardName ? `FN:${escapeVcard(form.vcardName)}` : null,
    form.vcardOrg ? `ORG:${escapeVcard(form.vcardOrg)}` : null,
    form.vcardPhone ? `TEL;TYPE=CELL:${escapeVcard(form.vcardPhone)}` : null,
    form.vcardEmail ? `EMAIL:${escapeVcard(form.vcardEmail)}` : null,
    url ? `URL:${escapeVcard(url)}` : null,
    'END:VCARD',
  ].filter(Boolean).join('\n')
}

function buildEmailPayload(form: FormState, errors: QrErrorMessages): string {
  const address = required(form.emailAddress, errors.emailRequired)
  const params = new URLSearchParams()
  if (form.emailSubject) params.set('subject', form.emailSubject)
  if (form.emailBody) params.set('body', form.emailBody)
  const query = params.toString()
  return `mailto:${address}${query ? `?${query}` : ''}`
}

function buildSmsPayload(form: FormState, errors: QrErrorMessages): string {
  const phone = required(form.smsPhone, errors.smsPhoneRequired)
  return `SMSTO:${phone}:${form.smsBody}`
}

function required(value: string, message: string): string {
  const trimmed = value.trim()
  if (!trimmed) {
    throw new QrInputError('empty_payload', message)
  }
  return trimmed
}

function escapeWifi(value: string): string {
  return value.replace(/([\\;,:"])/g, '\\$1')
}

function escapeVcard(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
}
